import { Box } from '@mui/material';
import UserDashboardBanner from './UserDashboardBanner';
import DeviceInfoSection from './DeviceInfoSection';
import MobileDeviceSection from './MobileDeviceSection';
import EmergencySettingsSection from './EmergencySettingsSection';
import UserDataSection from './UserDataSection';
import { getDFUInformation } from '@/repository/DFUDAO';
import { useUserContext } from '@/context_providers/user_context/UserProvider';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useDashboardUserAuthId,
  useDashboardDeviceOverride,
} from '@/stores/map/dashboardStore';
import { UserStatusPingViewModel } from '@/models/User/UserStatusPingViewModel';
import Group from '@/models/Organization/Group';
import {
  FirmwareLookupTableOnAzure,
  OrganizationOnAzure,
} from '../../../../../../generated-client/models';
import type { PvdConfigurationOnAzure } from '@/../generated-client/models';

interface DashboardTabProps {
  userStatus: UserStatusPingViewModel | null;
  value: number;
  organization: OrganizationOnAzure;
  groups: Array<Group>;
  pvdConfigurations: PvdConfigurationOnAzure[] | undefined;
  index: number;
  /** Non-Admin viewers (e.g. Dispatch) get a read-only dashboard — no tier/config mutation (Task 20/WD4). */
  isReadOnly?: boolean;
}

/**
 *
 * @param {DashboardTabProps} props
 * @returns
 */
const DashboardTab = ({
  userStatus,
  value,
  index,
  organization,
  groups,
  pvdConfigurations,
  isReadOnly = false,
}: DashboardTabProps) => {
  const dashboardUserAuthId = useDashboardUserAuthId();

  // Get active auth ID either from the location marker or from the dashboard store
  const activeUserAuthId = useMemo(() => {
    return dashboardUserAuthId;
  }, [dashboardUserAuthId]);

  const getUserInfo = useCallback(() => {
    if (!groups || !Array.isArray(groups) || !activeUserAuthId) return null;

    const userAuthId = activeUserAuthId;

    // Find the user in any group if no specific group is selected
    for (const group of groups) {
      const userDetails = group.group_value?.find(
        (user) => user.auth_id === userAuthId,
      );

      if (userDetails && group) {
        return {
          groupName: group.group_name || '',
          firstName: userDetails.first_name,
          lastName: userDetails.last_name,
          phoneNumber: userDetails.phone_number,
          role: userDetails.role,
          userAuthId: userAuthId,
          pvdConfigId: userDetails.pvd_config_id ?? null,
          emergencyConfigId: userDetails.emergency_config_id ?? null,
          orgUserAzureId: userDetails.org_user_azure_id ?? userDetails.id,
          locationPreference: userDetails.location_preference,
          languageCode: userDetails.language_code,
          shouldForceApplyConfig: userDetails.should_force_apply_config,
          deviceLocationPermission: userDetails.device_location_permission,
        };
      }
    }

    return null;
  }, [groups, activeUserAuthId]);

  const userInfo = useMemo(() => getUserInfo(), [getUserInfo]);

  const deviceOverride = useDashboardDeviceOverride();
  const isDeviceOnly = !userInfo && !!deviceOverride;

  const groupLocationSharing = useMemo(() => {
    if (!groups || !Array.isArray(groups) || !userInfo?.groupName) {
      return null;
    }

    const currentGroup = groups.find(
      (g) => g?.group_name === userInfo.groupName,
    );
    return currentGroup?.allow_group_location_tracking ?? null;
  }, [groups, userInfo?.groupName]);

  const { bearerToken } = useUserContext();

  const [firmwareLookup, setFirmwareLookup] =
    useState<FirmwareLookupTableOnAzure | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchLookup = async () => {
      const cachedData = sessionStorage.getItem('firmwareLookup');
      if (cachedData) {
        try {
          const parsedData = JSON.parse(cachedData);
          if (!cancelled) {
            setFirmwareLookup(parsedData);
          }
          return;
        } catch {
          sessionStorage.removeItem('firmwareLookup');
        }
      }
      try {
        const data = await getDFUInformation();

        if (!cancelled) {
          setFirmwareLookup(data);
          sessionStorage.setItem('firmwareLookup', JSON.stringify(data));
        }
      } catch {
        // Fail silently
      }
    };

    if (bearerToken) {
      fetchLookup();
    }

    return () => {
      cancelled = true;
    };
  }, [bearerToken]);

  if (value !== index) {
    return null;
  }

  return (
    <>
      {/* Banner Component */}
      {!isDeviceOnly && (
        <UserDashboardBanner
          userStatus={userStatus}
          userInfo={userInfo}
          groupLocationSharing={groupLocationSharing}
        />
      )}
      {/* Content */}
      <Box sx={{ p: 1 }}>
        {/* Main Content - Row-based Layout */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            p: { xs: 2, md: 1 },
          }}
        >
          {/* ROW 1 - Device Info & Mobile Device */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: {
                xs: 'column',
                lg: 'row',
              },
              gap: 2,
            }}
          >
            <DeviceInfoSection
              userStatus={isDeviceOnly ? null : userStatus}
              pvdConfigurations={pvdConfigurations}
              userInfo={userInfo}
              firmwareLookup={firmwareLookup}
              organization={organization}
              deviceOverride={deviceOverride}
              isReadOnly={isReadOnly}
            />
            {!isDeviceOnly && (
              <MobileDeviceSection
                userStatus={userStatus}
                firmwareLookup={firmwareLookup}
              />
            )}
          </Box>

          {/* ROW 2 - Emergency Settings & User Data */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: {
                xs: 'column',
                lg: 'row',
              },
              gap: 2,
            }}
          >
            {!isDeviceOnly && (
              <EmergencySettingsSection
                compassSettings={userStatus?.dto?.compass_settings}
                groups={groups}
                organization={organization}
              />
            )}
            {!isDeviceOnly && (
              <UserDataSection
                userInfo={userInfo}
                groups={groups}
                organization={organization}
              />
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default DashboardTab;
